import { Button } from "@ariakit/react";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import parse from "html-react-parser";
import { Fragment, useMemo, useState } from "react";
import * as styles from "./PaginatedText.css";

interface PaginatedTextProps {
	/** Encoded HTML string with paragraphs (e.g., "&lt;p&gt;...&lt;/p&gt;") */
	htmlContent: string;
	maxWordsPerParagraph?: number;
	maxWordsPerPage?: number;
	pageHeight?: string;
}

export default function PaginatedText({
	htmlContent,
	maxWordsPerParagraph = 70,
	maxWordsPerPage = 150,
	pageHeight = "260px",
}: PaginatedTextProps) {
	const [currentPage, setCurrentPage] = useState(0);

	const pages = useMemo(() => {
		// 1. Decode the HTML string.
		const decodeHtml = (encoded: string): string => {
			const textarea = document.createElement("textarea");
			textarea.innerHTML = encoded;
			return textarea.value;
		};
		const decodedHtml = decodeHtml(htmlContent);

		// 2. Extract <p> elements by inserting decoded HTML into a temporary container.
		const container = document.createElement("div");
		container.innerHTML = decodedHtml;
		const pElements = Array.from(container.getElementsByTagName("p"));

		const paragraphs: string[] =
			pElements.length > 0
				? pElements.map((p) => p.textContent?.trim() || "")
				: [container.textContent?.trim() || ""];

		// Helper: count the words in a string.
		const countWords = (text: string): number =>
			text.split(/\s+/).filter((word) => word.length > 0).length;

		// 3. Split a paragraph into sentences using punctuation boundaries.
		const splitIntoSentences = (paragraph: string): string[] => {
			// This regex matches sequences ending in a punctuation mark (. ! or ?)
			// or the last part of the paragraph.
			const regex = /([^.!?]+[.!?]+)|([^.!?]+$)/g;
			const matches = paragraph.match(regex);
			return matches
				? matches.map((s) => s.trim()).filter((s) => s.length > 0)
				: [];
		};

		// 4. Process paragraphs into an array of text chunks.
		const chunks: string[] = [];
		for (const paragraph of paragraphs) {
			if (countWords(paragraph) <= maxWordsPerParagraph) {
				chunks.push(paragraph);
			} else {
				const sentences = splitIntoSentences(paragraph);
				for (const sentence of sentences) {
					if (countWords(sentence) > maxWordsPerParagraph) {
						const words = sentence.split(/\s+/);
						for (let i = 0; i < words.length; i += maxWordsPerParagraph) {
							const chunk = words.slice(i, i + maxWordsPerParagraph).join(" ");
							chunks.push(chunk);
						}
					} else {
						chunks.push(sentence);
					}
				}
			}
		}

		// 5. Group the chunks into pages, ensuring each page’s word count is <= maxWordsPerPage.
		const pageGroups: string[] = [];
		let currentGroup = "";
		let currentGroupWordCount = 0;

		for (const chunk of chunks) {
			const wordsInChunk = countWords(chunk);
			// If adding the current chunk would exceed the limit, finalize the current group.
			if (
				currentGroupWordCount + wordsInChunk > maxWordsPerPage &&
				currentGroup
			) {
				pageGroups.push(currentGroup.trim());
				currentGroup = `${chunk} `;
				currentGroupWordCount = wordsInChunk;
			} else {
				currentGroup += `${chunk} `;
				currentGroupWordCount += wordsInChunk;
			}
		}

		if (currentGroup.trim()) {
			pageGroups.push(currentGroup.trim());
		}
		return pageGroups;
	}, [htmlContent, maxWordsPerParagraph, maxWordsPerPage]);

	return (
		<div
			className={styles.container}
			style={assignInlineVars({
				[styles.height]: pageHeight,
			})}
		>
			<div className={styles.page}>
				<p>{pages[currentPage] ? parse(pages[currentPage]) : <></>}</p>
			</div>
			{pages.length > 1 && (
				<div className={styles.pagination}>
					{pages.map((_, index) => {
						const key = `page-${index}`;
						return (
							<Fragment key={key}>
								<Button
									className={styles.button({ selected: currentPage === index })}
									onClick={() => setCurrentPage(index)}
								>
									{index + 1}
								</Button>
								{index < pages.length - 1 && (
									<span className={styles.separator} />
								)}
							</Fragment>
						);
					})}
				</div>
			)}
		</div>
	);
}
